import React from "react";
import { cn } from "@/lib/utils";
import { Package, Copy, Link2, AlertCircle } from "lucide-react";
import type { ResultCard, SyncResultCardsProps } from "./SyncResultCards.types";

const colorClasses: Record<string, string> = {
  emerald:
    "bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400",
  amber: "bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400",
  indigo:
    "bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400",
  rose: "bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400",
};

export const SyncResultCards: React.FC<SyncResultCardsProps> = ({
  results,
  isLoading,
  className,
}) => {
  const cards: ResultCard[] = [
    {
      key: "imported",
      label: "Products Imported",
      value: (results?.imported as number) ?? null,
      icon: Package,
      color: "emerald",
    },
    {
      key: "duplicates",
      label: "Duplicates Detected",
      value: (results?.duplicates as number) ?? null,
      icon: Copy,
      color: "amber",
    },
    {
      key: "mappings",
      label: "Canonical Mappings Generated",
      value: (results?.mappings as number) ?? null,
      icon: Link2,
      color: "indigo",
    },
    {
      key: "review",
      label: "Items Need Review",
      value: (results?.needsReview as number) ?? null,
      icon: AlertCircle,
      color: "rose",
    },
  ];

  return (
    <div
      data-testid="sync-result-cards"
      className={cn("grid grid-cols-2 lg:grid-cols-4 gap-4", className)}
    >
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.key}
            data-testid={`result-card-${card.key}`}
            className="p-4 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={cn("p-2 rounded-lg", colorClasses[card.color])}>
                <Icon className="w-5 h-5" />
              </div>
            </div>
            {isLoading ? (
              <div className="h-8 w-16 rounded bg-slate-200 dark:bg-slate-700 animate-pulse mb-1" />
            ) : (
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                {card.value != null ? card.value.toLocaleString() : "—"}
              </p>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {card.label}
            </p>
          </div>
        );
      })}
    </div>
  );
};
